import { EventOutreachSchemaType, ListPerson } from "../../../schema/list";
import { formatEventDate } from "../../EventOutreach/formatEventDate";
import { isNormalizedCompanyNameKey, resolveNormalizedCompanyName } from "./normalizedCompanyName";
import { getSuggestionLabel } from "./suggestions.utils";

/**
 * Shared event-outreach tokens (PREC-3520) for inline preview. Resolved from the list's event
 * (not the lead) so {event_name} etc. render with real values. {meeting_time} uses the first slot.
 */
const buildPreviewEventValues = (
	event?: EventOutreachSchemaType | null
): Record<string, string> => {
	if (!event?.enabled) {
		return {};
	}

	const values: Record<string, string> = {
		event_name: event.eventName ?? "",
		event_start: formatEventDate(event.startDate),
		event_end: formatEventDate(event.endDate),
		meeting_location: event.meetingLocation ?? "",
		meeting_link: event.meetingLink ?? "",
	};

	if (event.meetingTimes?.length) {
		values.meeting_time = event.meetingTimes[0];
	}

	event.customVariables?.forEach((variable) => {
		if (variable.key) {
			values[variable.key] = variable.value;
		}
	});

	return values;
};

export const replacePlaceholders = (
	text: string,
	lead: ListPerson,
	suggestions: string[],
	event?: EventOutreachSchemaType | null
) => {
	// Prefer the explicitly passed list event (sourced from the loaded list, e.g. currentList.event)
	// over the lead's nested copy, which is only populated as a side-effect of the list query and can
	// be stale on first paint of a node card.
	const eventValues = buildPreviewEventValues(event ?? lead?.list?.event);

	const processedSuggestions = suggestions
		.map((key) => ({
			text: getSuggestionLabel ? getSuggestionLabel(key, lead) : key,
			value: key,
		}))
		.filter((item) => item.text !== undefined);

	let resultTextValue = text.trim();

	// Resolve shared event tokens up-front so they render even when not in the suggestions list
	// (handles both {event_name} and {event_name|Label} forms).
	for (const [key, value] of Object.entries(eventValues)) {
		const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

		resultTextValue = resultTextValue.replace(
			new RegExp(`\\{${escapedKey}(\\|[^}]*)?\\}`, "g"),
			String(value)
		);
	}

	for (const suggestion of suggestions) {
		const processedSuggestion = processedSuggestions.find((item) => item.value === suggestion);

		const placeholderWithLabel = `{${suggestion}|${processedSuggestion?.text || suggestion}}`;

		const placeholderWithFallback = `{${suggestion}|${suggestion}}`;

		const placeholder = `{${suggestion}}`;
		const cleanKey = suggestion.split("|")[0];
		const objectPath = cleanKey.split("_");

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		let realValue: any = lead;

		for (const path of objectPath) {
			if (path === "column") {
				realValue = lead.columns?.find((column) => column.columnConfigId === +objectPath[1])?.value;
				if (realValue && realValue.answer) {
					realValue = realValue.answer;
				} else if (typeof realValue === "string" && realValue.length > 0) {
					realValue = realValue;
				} else {
					realValue = "Placeholder not found";
				}

				if (realValue) {
					break;
				}
			} else if (realValue) {
				realValue = Array.isArray(realValue[path]) ? realValue[path][0] : realValue[path];
			}
		}

		// The normalized company name is null until something resolves it, and the send falls back to
		// the raw company name rather than sending nothing — so the preview must, instead of printing
		// the string "null" (PREC-3923).
		const displayValue = isNormalizedCompanyNameKey(suggestion)
			? resolveNormalizedCompanyName(realValue, lead)
			: String(realValue);

		if (resultTextValue.includes(placeholder)) {
			resultTextValue = resultTextValue.replaceAll(placeholder, displayValue);
		}

		if (resultTextValue.includes(placeholderWithLabel)) {
			resultTextValue = resultTextValue.replaceAll(placeholderWithLabel, displayValue);
		}

		if (resultTextValue.includes(placeholderWithFallback)) {
			resultTextValue = resultTextValue.replaceAll(placeholderWithFallback, displayValue);
		}
	}

	return resultTextValue;
};
